// ═══════════════════════════════════════════════════════════════
// CULTIVATION UTILITIES — Shared constants, helpers, and styling
// Path: src/utils/cultivationUtils.js
// ═══════════════════════════════════════════════════════════════

import React from "react";
import {
  Heart,
  Star,
  Target,
  Coffee,
  Users,
  Phone,
  Mail,
  Calendar,
} from "lucide-react";
import { FONT_SIZES } from "../constants/appConfig";
import {
  calculateEngagement,
  getPathwayForScore,
  getPipelineStages,
  getStageForScore,
  resolveEffectivePathway,
  getMemberActivities,
  DEFAULT_SCORING_CONFIG,
} from "./engagementScoring";

// Re-export scoring utilities for convenience
export {
  calculateEngagement,
  getPathwayForScore,
  getPipelineStages,
  getStageForScore,
  resolveEffectivePathway,
  DEFAULT_SCORING_CONFIG,
};

// ═══════════════════════════════════════════════════════════════
// PIPELINE STAGE DEFINITIONS
// ═══════════════════════════════════════════════════════════════

export const SCORING_STAGES = getPipelineStages();

export const PROSPECT_STAGE = {
  key: "prospect",
  label: "Prospects",
  scoreRange: null,
  instruction: "Non-members being cultivated for membership",
};

export const ALL_PIPELINE_STAGES = [PROSPECT_STAGE, ...SCORING_STAGES];

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & STYLE MAPS
// ═══════════════════════════════════════════════════════════════

export const URGENCY_COLORS = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#3b82f6",
};

export const PIPELINE_URGENCY = {
  overdue: {
    bg: "#fef2f2",
    border: "#fca5a5",
    text: "#991b1b",
    dot: "#dc2626",
  },
  soon: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e", dot: "#d97706" },
  good: { bg: "#f0fdf4", border: "#86efac", text: "#166534", dot: "#16a34a" },
};

export const CONTACT_TYPE_ICONS = {
  meeting: "🤝",
  phone: "📞",
  email: "📧",
  event: "🎭",
  note: "📝",
  call: "📞",
  coffee: "☕",
  letter: "✉️",
  lunch: "🍽️",
  volunteering: "🙋",
  donation: "💝",
  text: "💬",
};

// ═══════════════════════════════════════════════════════════════
// COLOR HELPERS
// ═══════════════════════════════════════════════════════════════

export const getDaysColor = (days) => {
  if (days === null) return "#dc2626";
  if (days <= 14) return "#16a34a";
  if (days <= 30) return "#65a30d";
  if (days <= 60) return "#d97706";
  return "#dc2626";
};

export const getPipelineStageColors = (palette) => ({
  prospect: {
    color: "#ea580c",
    columnBg: "#fff7ed",
    headerBg: "#ea580c",
    borderColor: "#fed7aa",
  },
  cold: {
    color: palette.text3 || "#6b7280",
    columnBg: `${palette.text3 || "#6b7280"}08`,
    headerBg: palette.text2 || "#6b7280",
    borderColor: palette.cardBorder || "#d1d5db",
  },
  connect: {
    color: palette.accent2 || "#b45309",
    columnBg: `${palette.accent2 || "#b45309"}08`,
    headerBg: palette.accent2 || "#b45309",
    borderColor: `${palette.accent2 || "#b45309"}40`,
  },
  contribute: {
    color: palette.accent3 || "#047857",
    columnBg: `${palette.accent3 || "#047857"}08`,
    headerBg: palette.accent3 || "#047857",
    borderColor: `${palette.accent3 || "#047857"}40`,
  },
  lead: {
    color: palette.accent1 || "#6d28d9",
    columnBg: `${palette.accent1 || "#6d28d9"}08`,
    headerBg: palette.accent1 || "#6d28d9",
    borderColor: `${palette.accent1 || "#6d28d9"}40`,
  },
});

// ═══════════════════════════════════════════════════════════════
// NEXT BEST ACTION ENGINE
// ═══════════════════════════════════════════════════════════════

export const getNextAction = (member, engagement) => {
  const { score, daysSinceContact } = engagement;

  if (member.isDonor && (daysSinceContact === null || daysSinceContact > 60)) {
    return {
      action: "Stewardship touch — thank and update on chapter impact",
      icon: Heart,
      urgency: "high",
      type: "steward",
    };
  }
  if (member.isBoardMember && !member.isDonor) {
    return {
      action: "Board peer ask — personal conversation about giving",
      icon: Star,
      urgency: "high",
      type: "ask",
    };
  }
  if (score >= 40 && !member.isDonor) {
    return {
      action: "Engagement is strong — consider a personal giving conversation",
      icon: Target,
      urgency: "medium",
      type: "ask",
    };
  }
  if (score >= 30 && (daysSinceContact === null || daysSinceContact > 45)) {
    return {
      action: "Re-engage with event invitation or coffee meeting",
      icon: Coffee,
      urgency: "medium",
      type: "engage",
    };
  }
  if (!member.assignedDirector?.trim()) {
    return {
      action: "Assign a director to build relationship",
      icon: Users,
      urgency: "medium",
      type: "assign",
    };
  }
  if (score < 30 && member.assignedDirector?.trim()) {
    return {
      action: "Personal outreach — ask about their professional goals",
      icon: Phone,
      urgency: "low",
      type: "engage",
    };
  }
  if (engagement.totalContacts === 0) {
    return {
      action: "Make first contact — welcome email or intro call",
      icon: Mail,
      urgency: "medium",
      type: "engage",
    };
  }
  if (!member.email?.trim()) {
    return {
      action: "Obtain email address for ongoing communication",
      icon: Mail,
      urgency: "low",
      type: "data",
    };
  }
  return {
    action: "Schedule check-in to maintain relationship",
    icon: Calendar,
    urgency: "low",
    type: "engage",
  };
};

// ═══════════════════════════════════════════════════════════════
// PIPELINE ACTION LOGIC (persona-aware)
// ═══════════════════════════════════════════════════════════════

export const getPipelineAction = (member, engagement) => {
  const { score, daysSinceContact, totalContacts } = engagement;

  // Resolve the persona hint if available
  const personaHint = member._persona?.actionHint;

  if (member.pathway === "prospect") {
    if (totalContacts === 0) {
      const base = "New prospect — make first contact and invite to an event";
      return {
        text: personaHint ? `${base}\n💡 ${personaHint}` : base,
        urgency: "soon",
      };
    }
    if (daysSinceContact > 30)
      return {
        text: `${daysSinceContact} days — follow up before interest fades`,
        urgency: "overdue",
      };
    if (score >= 10)
      return {
        text: "Showing interest — consider inviting to join as member",
        urgency: "good",
      };
    return {
      text: "Continue building relationship — invite to next event",
      urgency: "good",
    };
  }

  // ── Standard pipeline actions ──
  let baseAction;

  if (totalContacts === 0)
    baseAction = {
      text: "Introduce yourself — no contact on record",
      urgency: "overdue",
    };
  else if (daysSinceContact > 90)
    baseAction = {
      text: `${daysSinceContact} days since last contact — reach out today`,
      urgency: "overdue",
    };
  else if (daysSinceContact > 30)
    baseAction = {
      text: `${daysSinceContact} days — schedule a check-in this week`,
      urgency: "soon",
    };
  else if (member.isBoardMember && !member.isDonor)
    baseAction = {
      text: "Board member, not yet a donor — personal ask conversation",
      urgency: "soon",
    };
  else if (score >= 80)
    baseAction = {
      text: "Strong relationship — thank them or ask for introduction",
      urgency: "good",
    };
  else if (score >= 30)
    baseAction = {
      text: "Good momentum — invite to next event or meeting",
      urgency: "good",
    };
  else if (daysSinceContact > 14)
    baseAction = {
      text: `${daysSinceContact} days — a quick email or call keeps momentum`,
      urgency: "soon",
    };
  else
    baseAction = {
      text: "Recently contacted — continue building relationship",
      urgency: "good",
    };

  // Append persona guidance when available
  if (personaHint) {
    return {
      ...baseAction,
      text: `${baseAction.text}\n💡 ${personaHint}`,
    };
  }

  return baseAction;
};

// ═══════════════════════════════════════════════════════════════
// CONTACT LOG HELPER
// ═══════════════════════════════════════════════════════════════

export const getLastContactLog = (member) => {
  const logs = member.contactLog || [];
  if (logs.length === 0) return null;
  return logs.reduce((latest, l) => {
    const d = new Date(l.date || l.createdAt);
    const ld = new Date(latest.date || latest.createdAt);
    return d > ld ? l : latest;
  });
};

// ═══════════════════════════════════════════════════════════════
// MEMBER ENRICHMENT — Used by multiple tabs
// Now accepts optional personas array to attach persona data
// ═══════════════════════════════════════════════════════════════

export const enrichMembers = (members, pathways, personas, { allGifts = [], allEventAttendance = [], allSponsorships = [] } = {}) => {
  return members
    .map((m) => {
      const memberActivities = getMemberActivities(m.id, allGifts, allEventAttendance, allSponsorships);
      const engagement = calculateEngagement(m, undefined, memberActivities);
      const nextAction = getNextAction(m, engagement);
      const memberLifetimeGiving = (memberActivities.memberGifts || []).reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
      const effective = resolveEffectivePathway(m, engagement.score, pathways, undefined, { lifetimeGiving: memberLifetimeGiving });

      // Attach resolved persona object as _persona for pipeline use
      const _persona =
        m.persona && (personas || []).length > 0
          ? personas.find((p) => p.id === m.persona) || null
          : null;

      return { ...m, engagement, nextAction, effective, _persona };
    })
    .sort((a, b) => b.engagement.score - a.engagement.score);
};

export const segmentMembers = (enriched) => {
  const invested = enriched.filter((m) => m.engagement.score >= 80);
  const engaged = enriched.filter(
    (m) => m.engagement.score >= 30 && m.engagement.score < 80
  );
  const warming = enriched.filter(
    (m) => m.engagement.score >= 10 && m.engagement.score < 30
  );
  const cold = enriched.filter((m) => m.engagement.score < 10);
  const donors = enriched.filter((m) => m.isDonor);
  const nonDonors = enriched.filter((m) => !m.isDonor);
  const readyForAsk = nonDonors.filter((m) => m.engagement.score >= 40);
  const needsAttention = enriched.filter(
    (m) =>
      m.engagement.daysSinceContact !== null &&
      m.engagement.daysSinceContact > 60 &&
      m.isDonor
  );
  const noContact = enriched.filter((m) => m.engagement.totalContacts === 0);
  const prospects = enriched.filter((m) => m.pathway === "prospect");
  return {
    invested,
    engaged,
    warming,
    cold,
    donors,
    nonDonors,
    readyForAsk,
    needsAttention,
    noContact,
    prospects,
  };
};

// ═══════════════════════════════════════════════════════════════
// PIPELINE BUCKET BUILDER — Used by Pipeline + Comms tabs
// ═══════════════════════════════════════════════════════════════

export const buildPipelineBuckets = (enriched, filterDirector, searchQuery) => {
  let filtered = enriched;
  if (filterDirector && filterDirector !== "all") {
    filtered = filtered.filter((m) => m.assignedDirector === filterDirector);
  }
  if (searchQuery?.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filtered = filtered.filter((m) => {
      const name = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
      const org = (m.organization || "").toLowerCase();
      const director = (m.assignedDirector || "").toLowerCase();
      return name.includes(q) || org.includes(q) || director.includes(q);
    });
  }

  const buckets = {
    prospect: [],
    cold: [],
    connect: [],
    contribute: [],
    lead: [],
  };
  filtered.forEach((m) => {
    if (m.pathway === "prospect") {
      buckets.prospect.push(m);
      return;
    }
    const effectiveKey =
      m.effective?.key || getStageForScore(m.engagement.score).key;
    const bucketKey = effectiveKey === "unengaged" ? "cold" : effectiveKey;
    if (buckets[bucketKey]) {
      buckets[bucketKey].push(m);
    } else {
      buckets.cold.push(m);
    }
  });
  return buckets;
};

// ═══════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════

export const EngagementMeter = ({ score, palette }) => {
  const color =
    score >= 80
      ? "#10b981"
      : score >= 30
      ? "#f59e0b"
      : score >= 10
      ? "#fb923c"
      : "#ef4444";
  const label =
    score >= 80
      ? "Lead"
      : score >= 30
      ? "Contribute"
      : score >= 10
      ? "Connect"
      : "Cold";
  const pct = Math.min((score / 150) * 100, 100);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          background: `${palette.accent1}10`,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 4,
            background: color,
            transition: "width 0.4s ease",
          }}
        />
      </div>
      <span
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          color,
          minWidth: 60,
          textAlign: "right",
        }}
      >
        {score} · {label}
      </span>
    </div>
  );
};

export const StatBubble = ({ value, label, color, palette }) => (
  <div style={{ textAlign: "center" }}>
    <div
      style={{
        fontSize: "1.6rem",
        fontWeight: 700,
        color: color || palette.accent1,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div
      style={{ fontSize: FONT_SIZES.xs, color: palette.text3, marginTop: 4 }}
    >
      {label}
    </div>
  </div>
);
